/* eslint-disable react/jsx-props-no-spreading */
import { React, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  createFilterOptions
} from '@mui/material';
import { ticketApi } from '../../../../api/ticketApi';

// Met une majuscule en début de chaîne
const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '');

// Formatage de date FR court (ex: 12/03/2026)
const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Label d'un apprenant : "Nom Prénom — #id"
const learnerLabel = (l) =>
  (l ? `${capitalize(l.lastname)} ${capitalize(l.firstname)} — #${l.id}` : '');

// Label d'une session : "Session 3 · 12/03/2026 → 14/03/2026"
const sessionLabel = (s) => {
  if (!s) return '';
  const num = s.number_session ? `Session ${s.number_session}` : `Session #${s.session_id}`;
  const dates = s.start_date ? ` · ${formatDate(s.start_date)}${s.end_date ? ` → ${formatDate(s.end_date)}` : ''}` : '';
  return `${num}${dates}`;
};

// Label d'un utilisateur affecté : "Nom Prénom — #id"
const assigneeLabel = (u) =>
  (u ? `${capitalize(u.lastname)} ${capitalize(u.firstname)} — #${u.id}` : '');

// Filtre du combobox apprenant : recherche par nom/prénom/id/email, limité à 100 résultats
const learnerFilter = createFilterOptions({
  limit: 100,
  stringify: (o) => `${o.firstname} ${o.lastname} ${o.id} ${o.email || ''}`
});

// Filtre du combobox affectation : recherche par nom/prénom/id
const assigneeFilter = createFilterOptions({
  limit: 100,
  stringify: (o) => `${o.firstname} ${o.lastname} ${o.id}`
});

/**
 * Formulaire partagé de réclamation client (création + édition), contrôlé par le parent.
 * - mode="create" : formulaire vierge -> ticketApi.createClientComplaint
 * - mode="edit"   : pré-rempli depuis `initial` (ligne de la liste) -> ticketApi.updateClientComplaint
 * `onSaved` est appelé après un enregistrement réussi (pour rafraîchir la liste).
 */
const ClientComplaintForm = (props) => {
  const { open, onClose, mode, initial, user, onSaved } = props;
  const isEdit = mode === 'edit';

  const [submitting, setSubmitting] = useState(false);

  const [learners, setLearners] = useState([]);
  const [loadingLearners, setLoadingLearners] = useState(false);
  const [selectedLearner, setSelectedLearner] = useState(null);

  const [assignees, setAssignees] = useState([]);
  const [loadingAssignees, setLoadingAssignees] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState(null);

  const [enrollments, setEnrollments] = useState([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [selectedFormation, setSelectedFormation] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);

  const [description, setDescription] = useState('');

  const loadLearners = async () => {
    try {
      setLoadingLearners(true);
      const data = await ticketApi.getComplaintLearners(localStorage.getItem('accessToken'));
      setLearners(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors du chargement des apprenants');
    } finally {
      setLoadingLearners(false);
    }
  };

  const loadAssignees = async () => {
    try {
      setLoadingAssignees(true);
      const data = await ticketApi.getComplaintAssignees(localStorage.getItem('accessToken'));
      setAssignees(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoadingAssignees(false);
    }
  };

  const loadEnrollments = async (learnerId) => {
    try {
      setLoadingEnrollments(true);
      const data = await ticketApi.getLearnerEnrollments(localStorage.getItem('accessToken'), learnerId);
      setEnrollments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors du chargement des formations de l\'apprenant');
    } finally {
      setLoadingEnrollments(false);
    }
  };

  // Initialisation à l'ouverture : chargement des listes + pré-remplissage en édition
  useEffect(() => {
    if (!open) return;
    loadLearners();
    loadAssignees();

    if (isEdit && initial) {
      setSelectedLearner(initial.learner_id ? {
        id: initial.learner_id,
        firstname: initial.customer ? initial.customer.firstname : '',
        lastname: initial.customer ? initial.customer.lastname : ''
      } : null);
      setSelectedFormation(initial.formation_id ? { id: initial.formation_id, name: initial.formation } : null);
      setSelectedSession(initial.session_id ? {
        session_id: initial.session_id,
        number_session: initial.session ? initial.session.number_session : null,
        start_date: initial.session ? initial.session.start_date : null,
        end_date: initial.session ? initial.session.end_date : null
      } : null);
      setSelectedAssignee(initial.user_in_charge_id ? {
        id: initial.user_in_charge_id,
        firstname: initial.user_in_charge ? initial.user_in_charge.firstname : '',
        lastname: initial.user_in_charge ? initial.user_in_charge.lastname : ''
      } : null);
      setDescription(initial.description || '');
      if (initial.learner_id) loadEnrollments(initial.learner_id);
    } else {
      setSelectedLearner(null);
      setSelectedFormation(null);
      setSelectedSession(null);
      setSelectedAssignee(null);
      setEnrollments([]);
      setDescription('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Formations distinctes de l'apprenant sélectionné
  const formationOptions = useMemo(() => {
    const map = new Map();
    enrollments.forEach((e) => {
      if (e.formation_id != null && !map.has(e.formation_id)) {
        map.set(e.formation_id, { id: e.formation_id, name: e.formation_name });
      }
    });
    return Array.from(map.values());
  }, [enrollments]);

  // Sessions de la formation choisie (pour cet apprenant)
  const sessionOptions = useMemo(() => {
    if (!selectedFormation) return [];
    return enrollments
      .filter((e) => e.formation_id === selectedFormation.id)
      .map((e) => ({
        session_id: e.session_id,
        number_session: e.number_session,
        start_date: e.start_date,
        end_date: e.end_date,
        status: e.status
      }));
  }, [enrollments, selectedFormation]);

  // Auto-sélection quand une seule formation est disponible
  useEffect(() => {
    if (selectedLearner && formationOptions.length === 1 && !selectedFormation) {
      setSelectedFormation(formationOptions[0]);
    }
  }, [formationOptions, selectedLearner, selectedFormation]);

  // Auto-sélection quand une seule session est disponible
  useEffect(() => {
    if (selectedFormation && sessionOptions.length === 1 && !selectedSession) {
      setSelectedSession(sessionOptions[0]);
    }
  }, [sessionOptions, selectedFormation, selectedSession]);

  const handleLearnerChange = (event, value) => {
    setSelectedLearner(value);
    setSelectedFormation(null);
    setSelectedSession(null);
    setEnrollments([]);
    if (value) loadEnrollments(value.id);
  };

  const handleFormationChange = (event, value) => {
    setSelectedFormation(value);
    setSelectedSession(null);
  };

  // Apprenant + texte obligatoires ; formation/session obligatoires si l'apprenant en a
  const canSubmit =
    !!selectedLearner &&
    description.trim() !== '' &&
    (formationOptions.length === 0 || (!!selectedFormation && !!selectedSession));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      const token = localStorage.getItem('accessToken');
      const payload = {
        learnerId: selectedLearner.id,
        formationId: selectedFormation ? selectedFormation.id : null,
        sessionId: selectedSession ? selectedSession.session_id : null,
        assignedUserId: selectedAssignee ? selectedAssignee.id : null,
        description: description.trim(),
        userId: user.id
      };

      const res = isEdit
        ? await ticketApi.updateClientComplaint(token, { ...payload, ticketId: initial.id })
        : await ticketApi.createClientComplaint(token, payload);

      if (res && res.status === 200) {
        toast.success(isEdit ? 'Réclamation modifiée avec succès!' : 'Réclamation ajoutée avec succès!');
        if (onSaved) onSaved();
        onClose();
      } else {
        toast.error((res && res.message) || 'Il y a eu un souci lors de l\'enregistrement !');
      }
    } catch (err) {
      console.error(err);
      toast.error('Il y a eu un souci lors de l\'enregistrement !');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog onClose={onClose} open={open} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 'bold', color: 'primary.main' }}>
        {isEdit ? 'Modifier la réclamation client' : 'Créer une réclamation client'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          {/* Apprenant */}
          <Autocomplete
            options={learners}
            loading={loadingLearners}
            value={selectedLearner}
            onChange={handleLearnerChange}
            filterOptions={learnerFilter}
            getOptionLabel={learnerLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="Aucun apprenant"
            loadingText="Chargement…"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Apprenant *"
                placeholder="Rechercher par nom, prénom ou id…"
                variant="outlined"
                fullWidth
              />
            )}
          />

          {/* Formation + Session côte à côte */}
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Autocomplete
              sx={{ flex: 1 }}
              options={formationOptions}
              loading={loadingEnrollments}
              value={selectedFormation}
              onChange={handleFormationChange}
              getOptionLabel={(o) => o.name || `Formation #${o.id}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={!selectedLearner}
              noOptionsText={selectedLearner ? 'Aucune formation' : 'Choisir un apprenant'}
              loadingText="Chargement…"
              renderInput={(params) => (
                <TextField {...params} label="Formation *" variant="outlined" fullWidth />
              )}
            />

            <Autocomplete
              sx={{ flex: 1 }}
              options={sessionOptions}
              value={selectedSession}
              onChange={(e, value) => setSelectedSession(value)}
              getOptionLabel={sessionLabel}
              isOptionEqualToValue={(option, value) => option.session_id === value.session_id}
              disabled={!selectedFormation}
              noOptionsText={selectedFormation ? 'Aucune session' : 'Choisir une formation'}
              renderInput={(params) => (
                <TextField {...params} label="Session *" variant="outlined" fullWidth />
              )}
            />
          </Box>

          {/* Affectation : utilisateur en charge (notifié) */}
          <Autocomplete
            options={assignees}
            loading={loadingAssignees}
            value={selectedAssignee}
            onChange={(e, value) => setSelectedAssignee(value)}
            filterOptions={assigneeFilter}
            getOptionLabel={assigneeLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="Aucun utilisateur"
            loadingText="Chargement…"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Affectation"
                placeholder="Rechercher un utilisateur par nom, prénom ou id…"
                variant="outlined"
                fullWidth
                helperText="L'utilisateur affecté sera notifié avec un lien vers la réclamation"
              />
            )}
          />

          {/* Texte de la réclamation */}
          <TextField
            label="Réclamation *"
            placeholder="Saisissez le détail de la réclamation…"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            variant="outlined"
            fullWidth
            multiline
            rows={5}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Typography sx={{ position: 'absolute', left: 16, color: 'primary.main', fontWeight: 'bold', fontSize: '0.8rem' }}>
          (*) Champ obligatoire
        </Typography>
        <Button onClick={onClose} color="primary" disabled={submitting}>
          Annuler
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" disabled={!canSubmit || submitting}>
          {isEdit ? 'Enregistrer' : 'Créer la réclamation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ClientComplaintForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['create', 'edit']),
  initial: PropTypes.object,
  user: PropTypes.object.isRequired,
  onSaved: PropTypes.func
};

ClientComplaintForm.defaultProps = {
  mode: 'create',
  initial: null,
  onSaved: null
};

export default ClientComplaintForm;
